import {useState} from 'react'
import {useFormik} from 'formik'
import {useTranslation} from 'react-i18next'
import useCybersourceFlex from '../../../hooks/cybersource/useCyberSourceFlex'
import {confirmCybersourcePayment} from '../../../../api'
import useFormSchema from './useFormSchema'

const useCreditCardForm = (invoiceId, authenticityToken, onSubmitPayment) => {
  const {t} = useTranslation('payments')
  const {
    cardDetails,
    cardNumberDetails,
    cardCvcDetails,
    createToken,
    acceptedCardBrands,
    isLoading: isLoadingForm = true,
    setupError,
    isValid: cybersourceFieldsValid,
  } = useCybersourceFlex()

  const [isSubmitting, setIsSubmiting] = useState(false)
  const [submitErrorMessage, setSubmitErrorMessage] = useState()

  const onSubmit = async (values, actions) => {
    setIsSubmiting(true)
    setSubmitErrorMessage(null)

    createToken(
      {
        expirationMonth: values.expirationMonth.padStart(2, '0'),
        expirationYear: values.expirationYear,
      },
      async ({token: transientToken, captureContext, error}) => {
        if (error) {
          error.invalidFieldKeys.forEach((key) => {
            actions.setFieldError(key, 'Invalid')
          })

          setSubmitErrorMessage(
            t('payments:CreditCardForm.transientTokenCreationError'),
          )
          return
        }

        const {payment = {}} = await confirmCybersourcePayment({
          payableType: 'Invoice',
          payableId: invoiceId,
          authenticityToken,
          transientToken,
          captureContext,
          payerDetails: {
            firstName: values.firstName,
            lastName: values.lastName,
          },
        })

        if (['cleared', 'settled'].includes(payment.status)) {
          await onSubmitPayment({
            type: 'cybersourceCreditCard',
            customerAccount: null,
          })
        } else {
          setSubmitErrorMessage(
            t(
              `payments:creditTransaction.failedReasonCodes.${payment.reasonCode}`,
              t('payments:creditTransaction.failedReasonCodes.default'),
            ),
          )
        }

        setIsSubmiting(false)
      },
    )
  }

  const formSchema = useFormSchema()
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      expirationMonth: '',
      expirationYear: '',
    },
    validationSchema: formSchema,
    onSubmit,
  })

  return {
    formik,
    cardDetails,
    cardNumberDetails,
    cardCvcDetails,
    acceptedCardBrands,
    isLoadingForm,
    setupError,
    isSubmitting,
    submitErrorMessage,
    isValid: formik.dirty && formik.isValid && cybersourceFieldsValid,
  }
}

export default useCreditCardForm
